import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Spinner,
  Form,
  InputGroup,
  Row,
  Col,
} from "react-bootstrap";
import {
  Eye,
  Pencil,
  Search,
  Funnel,
  ArrowClockwise,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const VARIANCE_BADGE = {
  over_budget: "danger",
  under_budget: "success",
};

const Cost = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);

  const [editTask, setEditTask] = useState(null);
  const [actualCost, setActualCost] = useState("");
  const [updating, setUpdating] = useState(false);

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH PROJECTS ================= */
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/adminCostVariance/projectCostVariance`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setProjects(res.data.data || []);
      }
    } catch {
      toast.error("Failed to fetch cost data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  /* ================= SEARCH + FILTER ================= */
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const searchText = `${p.projectName} ${p.projectLocation} ${p.user.name}`
        .toLowerCase();

      const matchesSearch = searchText.includes(searchTerm.toLowerCase());

      const matchesStatus =
        !filterStatus ||
        (filterStatus === "over_budget" && p.totals.totalVariance > 0) ||
        (filterStatus === "under_budget" && p.totals.totalVariance < 0);

      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, filterStatus]);

  /* ================= VIEW PROJECT ================= */
  const handleViewProject = async (projectId) => {
    try {
      setViewOpen(true);
      setViewLoading(true);

      const res = await axios.get(
        `${API_BASE}/adminCostVariance/projectCostVariance/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setProjectDetails(res.data);
      }
    } catch {
      toast.error("Failed to load project");
    } finally {
      setViewLoading(false);
    }
  };

  /* ================= UPDATE ACTUAL COST ================= */
  const handleUpdateCost = async () => {
    try {
      setUpdating(true);

      const res = await axios.put(
        `${API_BASE}/adminCostVariance/updateActualCost/${editTask.taskId}`,
        { actualCost },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        toast.success("Actual cost updated");
        handleViewProject(projectDetails.project.id);
        setEditTask(null);
      }
    } catch {
      toast.error("Failed to update cost");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer />

      {/* ===== HEADER (SAME AS USERS PAGE) ===== */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            <Col md={4}>
              <h4 className="fw-bold mb-0">Cost Management</h4>
              <small className="text-muted">Project Cost Variance</small>
            </Col>

            <Col md={4}>
              <InputGroup>
                <InputGroup.Text><Search /></InputGroup.Text>
                <Form.Control
                  placeholder="Search project / user / location"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col md={3}>
              <InputGroup>
                <InputGroup.Text><Funnel /></InputGroup.Text>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="over_budget">Over Budget</option>
                  <option value="under_budget">Under Budget</option>
                </Form.Select>
              </InputGroup>
            </Col>

            <Col md={1} className="text-end">
              <Button variant="outline-primary" onClick={fetchProjects}>
                <ArrowClockwise />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ===== TABLE ===== */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Project</th>
                  <th>Location</th>
                  <th>User</th>
                  <th>Total Bid</th>
                  <th>Total Actual</th>
                  <th>Variance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No data found
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((p) => (
                    <tr key={p.projectId}>
                      <td>{p.projectName}</td>
                      <td>{p.projectLocation}</td>
                      <td>{p.user.name}</td>
                      <td>${p.totals.totalBid}</td>
                      <td>${p.totals.totalActual}</td>
                      <td>
                        <Badge
                          bg={p.totals.totalVariance > 0 ? "danger" : "success"}
                        >
                          ${p.totals.totalVariance}
                        </Badge>
                      </td>
                      <td className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleViewProject(p.projectId)}
                        >
                          <Eye />
                        </Button>

                        <Button
                          size="sm"
                          variant="warning"
                          onClick={() => handleViewProject(p.projectId)}
                        >
                          <Pencil />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* ===== VIEW / EDIT PROJECT MODAL ===== */}
      <Modal show={viewOpen} onHide={() => setViewOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{projectDetails?.project?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            <Table bordered>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Bid</th>
                  <th>Actual</th>
                  <th>Variance</th>
                  <th>Status</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {projectDetails?.tasks.map((t) => (
                  <tr key={t.taskId}>
                    <td>{t.taskName}</td>
                    <td>${t.bidAmount}</td>
                    <td>${t.actualCost}</td>
                    <td>${t.variance}</td>
                    <td>
                      <Badge bg={VARIANCE_BADGE[t.status]}>
                        {t.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => {
                          setEditTask(t);
                          setActualCost(t.actualCost);
                        }}
                      >
                        <Pencil />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>

      {/* ===== UPDATE COST MODAL ===== */}
      <Modal show={!!editTask} onHide={() => setEditTask(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Actual Cost</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Control
              type="number"
              value={actualCost}
              onChange={(e) => setActualCost(e.target.value)}
              className="mb-3"
            />
            <Button
              className="w-100"
              onClick={handleUpdateCost}
              disabled={updating}
            >
              {updating ? <Spinner size="sm" /> : "Update Cost"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Cost;
